<template>
  <main class="container-wide">
    <div class="summary-header">
      <h1 class="page-title mb-0" data-testid="page-title">
        Review Your Package
      </h1>

      <p class="intro">
        <span data-testid="review-housemate-count">
          For {{ housemateCount }} housemate<template v-if="housemateCount > 1"
            >s</template
          >
          at
        </span>
        <strong data-testid="review-address">{{ address }}</strong>
      </p>
    </div>

    <BackButton class="back-link" />

    <ServiceSummary
      :joining-tenant="false"
      :ppppw="pricePerPersonPerWeek"
      :ppppm="pricePerPersonMonthly"
      :discount-description="discountDescription"
      :non-discounted-pppw="nonDiscountedPricePerPersonPerWeek"
      :non-discounted-ppppm="nonDiscountedPricePerPersonPerMonthly"
      :tenancy-start-date="tenancyStartDate"
      :tenancy-end-date="tenancyEndDate"
      :first-payment-date="tenancyStartDate"
      :broadband-date="broadbandInstallationDate"
    >
      <div class="discount-code-wrapper" v-if="shouldShowVouchers">
        <h2 class="content-panel__title">Applied vouchers</h2>
        <discount-activation
          v-model.trim="discountCode"
          @apply="applyDiscountCode"
          @clear="clearDiscountCode"
        />
      </div>
    </ServiceSummary>

    <button
      id="full-summary-next"
      class="btn btn--big next-btn"
      type="submit"
      @click="trackSubmitBtn"
      data-testid="next-button"
    >
      Next
    </button>
  </main>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import ServiceSummary from "@/components/ServiceSummary";
import DiscountActivation from "@/components/DiscountActivation.vue";
import {
  initialDiscountState,
  initialPackagePrice,
} from "@/store/initialState";
import axiosInstance from "@/utility/axiosInstance";

export default {
  name: "FullSummary",
  computed: {
    ...mapState({
      discount: (state) => state.discount,
      packagePrice: (state) => state.packagePrice,
      housemateCount: (state) => state.housemateCount,
      tenancyStartDate: ({ houseInformation }) => houseInformation.startDate,
      tenancyEndDate: ({ houseInformation }) => houseInformation.endDate,
      broadbandInstallationDate({ houseInformation }) {
        if (this.hasBroadband && !houseInformation.broadbandUrgent) {
          return houseInformation.broadbandDate;
        } else if (this.hasBroadband) {
          return this.tenancyStartDate;
        } else {
          return null;
        }
      },
    }),
    ...mapGetters({
      address: "getCustomerAddressString",
      hasBroadband: "hasBroadband",
      hasUsedDiscount: "hasUsedDiscount",
      pricePerPerson: "pricePerPerson",
      nonDiscountedPricePerPerson: "nonDiscountedPricePerPerson",
      pricePerPersonMonth: "pricePerPersonPerMonth",
      nonDiscountedPricePerPersonMonth: "nonDiscountedPricePerPersonPerMonth",
      shouldShowVouchers: "shouldShowVouchers",
    }),
    ...mapActions(["activateDiscount"]),
    pricePerPersonPerWeek() {
      return (this.pricePerPerson / 100).toFixed(2);
    },
    nonDiscountedPricePerPersonPerWeek() {
      return this.discount.isValid
        ? (this.nonDiscountedPricePerPerson / 100).toFixed(2)
        : null;
    },
    pricePerPersonMonthly() {
      let amountInPounds = this.pricePerPersonMonth / 100;
      return (Math.ceil(amountInPounds * 100) / 100).toFixed(2);
    },
    nonDiscountedPricePerPersonPerMonthly() {
      let amountInPounds = this.nonDiscountedPricePerPersonMonth / 100;
      let amount = Math.ceil(amountInPounds * 100) / 100;
      return this.discount.isValid ? amount : null;
    },
    discountDescription() {
      return this.isValid ? this.discount.description : null;
    },
    discountCode: {
      get() {
        return this.$store.state.discount.code;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { code: value });
      },
    },
    discountApplied: {
      get() {
        return this.$store.state.discount.isValid;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { isValid: value });
      },
    },
    isDiscountValidating: {
      get() {
        return this.$store.state.discount.isValidating;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { isValidating: value });
      },
    },
  },
  created() {
    //Resubmit discount for validation
    if (this.discountApplied) {
      this.applyDiscountCode();
    }
  },
  methods: {
    trackSubmitBtn() {
      this.trackGoogleEvent(
        "Click Let's Do It",
        "Create Account",
        "Confirm summary",
      );

      this.$router.push("/direct-debit");
    },
    applyDiscountCode() {
      this.$set(this, "isDiscountValidating", true);

      axiosInstance
        .post(`/voucher/${this.$store.state.discount.code}`, {
          selectedProducts: this.$store.getters.selectedProductPriceIds,
          housemateCount: this.$store.state.housemateCount,
          contractStartDate: this.tenancyStartDate,
          contractEndDate: this.tenancyEndDate,
          leadId: this.$store.state.leadId,
        })
        .then(({ data }) => {
          this.$set(this, "discountApplied", true);

          this.$store.commit("SET_PACKAGE_PRICE", {
            fullPriceAmount: data.priceBeforeDiscount,
            discountedPriceAmount: data.priceAfterDiscount,
          });

          this.$store.commit("SET_DISCOUNT_STATE", {
            description: data.message,
            errorMsg: "",
          });

          this.trackGoogleEvent(
            "Voucher applied successfully",
            "Voucher Apply",
            "Add Voucher",
          );
        })
        .catch((err) => {
          this.$set(this, "discountApplied", false);
          this.$store.commit("SET_DISCOUNT_STATE", {
            errorMsg:
              err.response.data.message ||
              "Invalid voucher code. Check and try again.",
          });

          this.clearDiscountCode(false);

          this.trackGoogleEvent(
            `Voucher applied failed`,
            "Voucher Apply",
            "Add Voucher",
          );
        })
        .finally(() => {
          this.$set(this, "isDiscountValidating", false);
        });
    },
    clearDiscountCode(resetDiscountState = true) {
      this.$store.commit("SET_PACKAGE_PRICE", initialPackagePrice());

      if (resetDiscountState) {
        this.$store.commit("SET_DISCOUNT_STATE", initialDiscountState());
      }
    },
  },
  components: {
    ServiceSummary,
    DiscountActivation,
  },
};
</script>

<style lang="scss" scoped>
main {
  max-width: 80rem;
}

.summary-header {
  .intro {
    font-size: 16px;
    line-height: 1.8;
    margin: 0 0 $spacing-small 0;
    color: $grey-dark;
    text-align: center;

    strong {
      color: $brand-dark-blue;
    }
  }
}

.next-btn {
  @include media("<medium") {
    width: calc(100vw - (#{$spacing-medium} * 2));
  }
}

.discount-code-wrapper {
  margin-top: 1em;

  h2 {
    margin-bottom: 0.3em;
  }
}
</style>
